import React, { Fragment, useEffect } from "react";
import { Link } from "react-router-dom";

import Header from "../../../components/Header/Header";
import PageTitle from "../../../components/pagetitle/PageTitle";
import Footer from "../../../components/footer/Footer";
import Scrollbar from "../../../components/scrollbar/scrollbar";

import "./EDADesignAutomation.css";
import "./PageTheme.css";
import serviceImage from "../../../images/integrated-engineering-expertise-visual.png";

type Block = { title?: string; text?: string; lines?: string[]; raw: string };

const config = {
  "title": "EDA & Design Automation",
  "category": "Validation, Automation & Product Engineering",
  "visual": "automation",
  "folder": "EDADesignAutomation",
  "sections": [
    [
      "EDA & Design Automation",
      "Engineering Smarter Design Workflows Through Automation\nEfficient semiconductor development depends on well-orchestrated engineering workflows as much as technical expertise. EDA & Design Automation streamlines complex design processes by automating repetitive tasks, integrating engineering tools, and standardizing execution across the development lifecycle. The result is improved productivity, greater consistency, and faster engineering outcomes.\n\nDigicomm Semiconductor delivers specialized EDA & Design Automation services that help engineering organizations optimize design flows and improve operational efficiency. From custom automation frameworks and workflow integration to script development, tool customization, and process optimization, our engineers build solutions that simplify execution, reduce manual intervention, and support scalable semiconductor development.\n\nPrimary CTA\n\nDiscuss Your Design Automation Requirements"
    ],
    [
      "EDA & Design Automation",
      "EDA & Design Automation\nAs semiconductor designs become increasingly complex, engineering teams must manage large-scale design flows, multiple EDA environments, and repetitive execution tasks while maintaining consistency and quality. EDA & Design Automation addresses these challenges by automating workflows, integrating engineering tools, and streamlining development processes, enabling teams to focus more on innovation and less on manual execution.\n\nDigicomm Semiconductor provides comprehensive EDA & Design Automation services that enhance engineering productivity across the semiconductor development lifecycle. Our engineers develop custom automation frameworks, workflow orchestration solutions, tool integrations, and productivity utilities that improve execution efficiency, standardize engineering practices, and support scalable design environments.\n\nOur EDA & Design Automation Capabilities\nDesign Flow Automation\nCustom EDA Tool Development\nWorkflow Integration\nAutomation Script Development\nTool Customization & Enhancement\nRegression & Batch Execution Automation\nEngineering Productivity Utilities\nProcess Standardization & Flow Optimization"
    ],
    [
      "Why EDA & Design Automation Matters",
      "Why EDA & Design Automation Matters\nSemiconductor development involves thousands of engineering tasks executed across multiple tools, teams, and design stages. As projects become more complex, manual workflows can introduce inconsistencies, consume valuable engineering time, and slow overall development. EDA & Design Automation addresses these challenges by creating intelligent, repeatable workflows that improve productivity, reduce manual effort, and enable more efficient engineering execution.\n\nBy automating routine processes, integrating EDA environments, and standardizing design flows, engineering teams can focus on solving complex technical problems while maintaining consistency across projects. A well-designed automation strategy not only accelerates development but also enhances quality, scalability, and engineering confidence throughout the semiconductor lifecycle.\n\nKey Outcomes\nImproved Engineering Productivity\n\nAutomate repetitive engineering tasks, allowing teams to focus on high-value design and development activities.\n\nStandardized Design Workflows\n\nEstablish consistent engineering processes that improve execution quality and reduce variability across projects.\n\nReduced Manual Effort\n\nMinimize repetitive interventions through intelligent automation, improving efficiency and reducing the potential for human error.\n\nAccelerated Project Execution\n\nStreamline engineering workflows to shorten development cycles and improve overall program delivery.\n\nScalable Engineering Environments\n\nDevelop automation frameworks that support growing design complexity, larger engineering teams, and evolving project requirements.\n\nEnhanced Engineering Quality\n\nImprove consistency, traceability, and workflow reliability through disciplined automation and integrated engineering processes."
    ],
    [
      "Our EDA & Design Automation Capabilities",
      "Our EDA & Design Automation Capabilities\nEffective engineering automation goes beyond scripting individual tasks. It requires designing connected workflows that integrate tools, standardize execution, automate repetitive processes, and provide greater visibility across the semiconductor development lifecycle. Digicomm delivers scalable automation solutions that improve engineering efficiency while supporting consistent, high-quality execution.\n\nDesign Flow Automation\n\nDevelop automated design flows that streamline engineering activities, eliminate repetitive manual tasks, and improve execution efficiency across semiconductor development.\n\nCustom EDA Tool Development\n\nDesign and build specialized engineering utilities and applications that address unique workflow requirements and enhance engineering productivity.\n\nWorkflow Integration\n\nConnect multiple EDA tools and engineering environments into unified workflows that enable seamless data exchange and process continuity.\n\nAutomation Script Development\n\nDevelop robust automation scripts for design execution, job management, reporting, and workflow orchestration using industry-standard scripting languages.\n\nTool Customization & Enhancement\n\nExtend and customize existing EDA environments to align with customer-specific engineering methodologies and project requirements.\n\nRegression & Batch Execution Automation\n\nAutomate regression runs, batch processing, and validation workflows to improve consistency, reduce execution time, and optimize engineering resources.\n\nEngineering Dashboards & Reporting\n\nCreate automated dashboards and reporting frameworks that provide real-time visibility into workflow status, execution metrics, and engineering progress.\n\nProcess Standardization & Flow Optimization\n\nEstablish standardized engineering methodologies and optimized workflows that improve collaboration, maintain consistency, and support scalable semiconductor development."
    ],
    [
      "Why Digicomm for EDA & Design Automation",
      "Why Digicomm for EDA & Design Automation\nEngineering automation is most effective when it aligns with the way design teams work. Digicomm develops automation solutions that integrate seamlessly into existing engineering environments, simplify complex workflows, and support consistent execution across semiconductor programs. Our approach combines technical expertise with a deep understanding of semiconductor design processes, enabling automation that delivers measurable improvements in productivity, quality, and scalability.\n\nWhat Sets Us Apart\nSemiconductor Workflow Expertise\n\nAutomation solutions developed with a strong understanding of semiconductor design methodologies, engineering processes, and EDA environments.\n\nScalable Automation Frameworks\n\nReusable and extensible automation architectures designed to support evolving projects, growing engineering teams, and increasing design complexity.\n\nSeamless Tool Integration\n\nExpertise in connecting diverse EDA tools and engineering environments into unified workflows that improve continuity and operational efficiency.\n\nEngineering-Centric Development\n\nAutomation solutions built around engineering requirements, ensuring they enhance productivity without disrupting established design practices.\n\nReliable Execution & Long-Term Value\n\nA disciplined approach to automation that improves process consistency, reduces operational overhead, and supports sustainable engineering excellence across the product development lifecycle."
    ],
    [
      "Technology Expertise",
      "Technology Expertise\nModern semiconductor engineering relies on connected workflows that bring together EDA tools, automation frameworks, scripting environments, and engineering infrastructure into a unified development ecosystem. Digicomm's automation expertise spans the technologies that enable efficient design execution, workflow standardization, and engineering productivity across the semiconductor lifecycle.\n\nEDA Platforms\nSynopsys Tool Ecosystem\nCadence Tool Ecosystem\nSiemens EDA (Mentor Graphics)\nMulti-Vendor Design Environments\nCustom Engineering Utilities\nAutomation & Scripting\nPython\nTcl\nPerl\nShell Scripting\nAPI-Based Automation\nWorkflow Orchestration\nDesign Flow Automation\nJob Scheduling & Execution\nRegression Automation\nBatch Processing\nWorkflow Integration\nEngineering Infrastructure\nLinux Development Environments\nHigh-Performance Computing (HPC) Clusters\nDistributed Compute Environments\nVersion Control Systems\nLicense Management Integration\nProductivity & Collaboration\nEngineering Dashboards\nAutomated Reporting\nCI/CD Pipeline Integration\nIssue Tracking & Workflow Management\nProcess Monitoring & Analytics"
    ],
    [
      "Transform Engineering Productivity Through Intelligent Automation",
      "Transform Engineering Productivity Through Intelligent Automation\nAs semiconductor programs become increasingly complex, engineering efficiency becomes a critical driver of successful product development. Digicomm Semiconductor helps organizations build intelligent automation solutions that streamline design workflows, integrate engineering environments, and improve execution consistency across the development lifecycle. Whether you're optimizing existing processes or building scalable automation frameworks, our engineering teams deliver solutions that enable faster, more reliable semiconductor development.\n\nPrimary CTA\n\nDiscuss Your Design Automation Requirements"
    ]
  ]
} as const;

const splitBlocks = (raw: string): string[] =>
  raw.split(/\n\s*\n/g).map((x) => x.trim()).filter(Boolean);

const asBlock = (raw: string): Block => {
  const lines = raw.split("\n").map((x) => x.trim()).filter(Boolean);
  if (lines.length >= 2 && lines[0].length <= 74) {
    const rest = lines.slice(1);
    const listLike = rest.length >= 3 && rest.every((x) => x.length < 78);
    if (listLike) return { title: lines[0], lines: rest, raw };
    return { title: lines[0], text: rest.join(" "), raw };
  }
  if (lines.length === 1) return { text: lines[0], raw };
  return { text: lines.join(" "), raw };
};

const getHero = (section: readonly string[], pageTitle: string) => {
  const merged = section.join("\n\n");
  const blocks = splitBlocks(merged);
  const filtered = blocks.filter((x) => x !== "Primary CTA");
  if (filtered[0] === pageTitle) filtered.shift();

  const first = filtered.shift() || pageTitle;
  const firstLines = first.split("\n").map((x) => x.trim()).filter(Boolean);
  const headline = firstLines[0] || pageTitle;
  const body: string[] = [];

  if (firstLines.length > 1) body.push(firstLines.slice(1).join(" "));
  filtered.forEach((b) => body.push(b));

  let cta = "";
  if (body.length && body[body.length - 1].length < 85) cta = body.pop() || "";
  return { headline, body, cta };
};

const Arrow = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h13M13 7l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const EngineeringVisual: React.FC = () => (
  <div className="ses-visual service-engineering-image">
    <img src={serviceImage} alt="Integrated semiconductor engineering capabilities" />
  </div>
);

const ContentPanel: React.FC<{ raw: string; index: number }> = ({ raw, index }) => {
  const blocks = splitBlocks(raw).filter((x) => x !== "Primary CTA");
  return (
    <div className="ses-block-grid">
      {blocks.map((rawBlock, i) => {
        const block = asBlock(rawBlock);
        const long = rawBlock.length > 430;
        const labelOnly =
          !block.title &&
          !!block.text &&
          block.text.length < 80 &&
          (/[:：]$/.test(block.text) || i === 0);

        return (
          <article
            className={`ses-block ${long ? "ses-block--wide" : ""} ${labelOnly ? "ses-block--label" : ""}`}
            key={`${index}-${i}`}
          >
            {block.title && <h3>{block.title}</h3>}
            {block.text && (labelOnly ? <h3>{block.text}</h3> : <p>{block.text}</p>)}
            {block.lines && <ul>{block.lines.map((line) => <li key={line}>{line}</li>)}</ul>}
          </article>
        );
      })}
    </div>
  );
};

const RelatedPanel: React.FC<{ raw: string }> = ({ raw }) => {
  const blocks = splitBlocks(raw);
  const routeMap: Record<string, string> = {
    "Analog & Mixed Signal Design & Layout": "/services/analog-mixed-signal",
    "Analog & Mixed Signal": "/services/analog-mixed-signal",
    "Static Timing Analysis (STA)": "/services/static-timing-analysis",
    "FPGA Engineering": "/services/fpga-engineering",
    "Embedded Software Systems": "/services/embedded-software-systems",
    "Hardware Platform Engineering": "/services/hardware-platform-engineering",
    "Networking Solutions": "/services/networking-solutions",
    "Silicon Validation": "/services/silicon-validation",
    "EDA & Design Automation": "/services/eda-design-automation",
    "Product Engineering": "/services/product-engineering",
    "ASIC & SoC Design": "/services/asic-soc-design",
    "Physical Design": "/services/physical-design",
    "Verification Services": "/services/verification",
  };

  const cards: React.ReactNode[] = [];
  let group = "";

  blocks.forEach((b, i) => {
    if (/^Related .*:$/.test(b.trim())) {
      group = b.trim();
      return;
    }
    if (b === "Learn More →") return;

    const lines = b.split("\n").map((x) => x.trim()).filter(Boolean);
    const title = lines[0];
    const desc = lines.slice(1).join(" ");

    if (routeMap[title]) {
      cards.push(
        <Link className="ses-related-card" to={routeMap[title]} key={`${title}-${i}`}>
          <span>{group}</span>
          <h3>{title}</h3>
          {desc && <p>{desc}</p>}
          <em>Learn More <Arrow/></em>
        </Link>
      );
    } else {
      cards.push(
        <article className="ses-related-card" key={`${title}-${i}`}>
          <span>{group}</span>
          <h3>{title}</h3>
          {desc && <p>{desc}</p>}
        </article>
      );
    }
  });

  return <div className="ses-related-grid">{cards}</div>;
};

const Page: React.FC = () => {
  const sections = config.sections as readonly (readonly string[])[];
  const hero = getHero(sections[0] || [], config.title);

  const lastSection = sections[sections.length - 1] || [];
  const lastRaw = lastSection.join("\n\n");
  const lastBlocks = splitBlocks(lastRaw).filter((x) => x !== "Primary CTA");
  const ctaLabel =
    lastBlocks.length && lastBlocks[lastBlocks.length - 1].length < 90
      ? lastBlocks[lastBlocks.length - 1]
      : "";
  const ctaContent = ctaLabel ? lastBlocks.slice(0, -1) : lastBlocks;
  const hasRelated = sections.length === 9;

  useEffect(() => {
    document.title = `${config.title} | Digicomm Semiconductor`;
  }, []);

  return (
    <Fragment>
      <div className={`specialized-service-page inner-page automation-redesign`}>
        <div className="body_wrap o-clip">
          <Header />

          <main>
            <PageTitle pageTitle={config.title} pagesub={config.title} />

            <section className="ses-hero">
              <div className="ses-shell ses-two-col">
                <div className="ses-hero__copy">
                  <h1>{hero.headline}</h1>
                  {hero.body.map((p, i) => <p key={i}>{p}</p>)}
                  {hero.cta && (
                    <Link className="ses-btn" to="/contact">
                      {hero.cta}<Arrow/>
                    </Link>
                  )}
                </div>
                <EngineeringVisual />
              </div>
            </section>

            {sections.slice(1, hasRelated ? -2 : -1).map((section, idx) => {
              const raw = section.join("\n\n");
              const blocks = splitBlocks(raw);
              const lead = blocks[0] || "";
              const firstLine = lead.split("\n")[0] || "";

              return (
                <section className={`ses-section ses-section--${idx + 2} ${idx % 2 ? "ses-section--alt" : ""}`} key={idx}>
                  <div className="ses-shell">
                    <div className="ses-section-head">
                      <span>{String(idx + 2).padStart(2, "0")}</span>
                      <h2>{firstLine}</h2>
                    </div>
                    <ContentPanel raw={raw} index={idx}/>
                  </div>
                </section>
              );
            })}

            {hasRelated && (
              <section className="ses-section ses-related">
                <div className="ses-shell">
                  <RelatedPanel raw={sections[sections.length - 2].join("\n\n")}/>
                </div>
              </section>
            )}

            <section className="ses-cta">
              <div className="ses-shell ses-two-col">
                <div className="ses-cta__copy">
                  {ctaContent.map((b, i) => {
                    const lines = b.split("\n").map((x) => x.trim()).filter(Boolean);
                    return i === 0
                      ? <h2 key={i}>{lines.join(" ")}</h2>
                      : <p key={i}>{lines.join(" ")}</p>;
                  })}

                  {ctaLabel && (
                    <Link className="ses-btn" to="/contact">
                      {ctaLabel}<Arrow/>
                    </Link>
                  )}
                </div>
                <EngineeringVisual />
              </div>
            </section>
          </main>

          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default Page;

